import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import roastPoster from "@/assets/sunday-roast.jpg";
import pastryPoster from "@/assets/pastry.jpg";

const EASE = [0.16, 1, 0.3, 1];

function PromoFrame({ img, alt, index, title, line, y, className }) {
  return (
    <motion.div
      style={{ y }}
      className={`relative ${className}`}
      data-testid={`promo-frame-${index}`}
    >
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.15 }}
        transition={{ duration: 1, ease: EASE }}
        className="border border-line bg-ink2 p-3 shadow-[0_50px_100px_-50px_rgba(0,0,0,0.9)] md:p-4"
      >
        <img src={img} alt={alt} loading="lazy" className="w-full object-cover" />
      </motion.div>
      <div className="mt-5 flex items-baseline gap-4">
        <span className="font-serif text-lg italic text-sage-dim">0{index}</span>
        <div>
          <p className="font-serif text-2xl font-medium text-cream md:text-3xl">{title}</p>
          <p className="mt-1.5 font-sans text-xs uppercase tracking-[0.25em] text-sage-dim">
            {line}
          </p>
        </div>
      </div>
    </motion.div>
  );
}

export default function Promos() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const yA = useTransform(scrollYProgress, [0, 1], [50, -50]);
  const yB = useTransform(scrollYProgress, [0, 1], [90, -90]);

  return (
    <section
      ref={ref}
      className="relative overflow-hidden border-t border-line/50 py-24 md:py-32"
      data-testid="promos-section"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-1/2 top-1/2 h-[70vh] w-[70vw] -translate-x-1/2 -translate-y-1/2 rounded-full bg-sage/5 blur-3xl"
      />
      <div className="relative mx-auto max-w-6xl px-6 md:px-12">
        <div className="max-w-2xl">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.8, ease: EASE }}
            className="font-sans text-xs uppercase tracking-[0.35em] text-sage"
          >
            On at The Moon
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.1 }}
            className="mt-5 font-serif text-4xl font-medium leading-tight text-cream sm:text-5xl md:text-6xl"
          >
            While the site is being built, the offers are already on.
          </motion.h2>
        </div>

        <div className="mt-16 md:mt-24">
          <PromoFrame
            img={roastPoster}
            alt="Sunday Roast — free dessert with every Sunday Roast, chosen by our chef"
            index={1}
            title="Sunday Roast"
            line="Free dessert with every roast"
            y={yA}
            className="md:w-[72%]"
          />
          <PromoFrame
            img={pastryPoster}
            alt="Buy a hot drink, get a pastry for £2 — all butter croissant, pain au chocolat or almond croissant"
            index={2}
            title="Hot drink + pastry"
            line="Get a pastry for £2"
            y={yB}
            className="md:-mt-28 md:ml-auto md:w-[62%]"
          />
        </div>
      </div>
    </section>
  );
}
